import { getFileName } from "@/Utils/commonExportFunctions";
import { EXCEL_FORMAT } from "@/constants/general";
import { UNIT_LABELS } from "@/pages/reporting_v2/constants";
import { translate } from "@/translations/functions/hook";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import type { TableRow } from "../../../../types";
import {
  COL_WIDTH,
  FILE_NAME_KEYS,
  ROW_HEIGHT,
  type ReportingExportProps,
  TOP_BORDER,
  formatTimeframeDate,
  getDownstreamName,
  getPeriodExportLabel,
} from "./common";

type LabelColumn = {
  header: string;
  value: (row: TableRow) => string;
};

const getLabelColumns = ({
  isIndividual,
  showZones,
  nameLabel,
}: ReportingExportProps): LabelColumn[] => {
  if (!isIndividual) {
    return [
      {
        header: "reportingPage:reportingColumnPeriod",
        value: (row) => getPeriodExportLabel(row),
      },
    ];
  }

  return [
    { header: nameLabel, value: (row) => row.name },
    ...(showZones
      ? [
          {
            header: "reportingPage:reportingColumnZone",
            value: (row: TableRow) => row.zone ?? "",
          },
        ]
      : []),
    { header: `${nameLabel}ID`, value: (row) => row.id ?? "" },
  ];
};

const mergeLabelCells = (
  sheet: ExcelJS.Worksheet,
  row: number,
  nrLabelColumns: number,
) => {
  if (nrLabelColumns > 1) sheet.mergeCells(row, 1, row, nrLabelColumns);
};

const generateExcelFile = (props: ReportingExportProps) => {
  const {
    rows,
    totalsRow,
    wasteColumns,
    unit,
    isIndividual,
    isPortfolio,
    title,
    selection,
    timeframe,
    flatDownstreamData,
  } = props;

  const labelColumns = getLabelColumns(props);
  const nrLabelColumns = labelColumns.length;
  // Downstream handling is configured per building, so portfolio view has no row for it.
  const showDownstream = !isPortfolio;
  const firstDataRow = showDownstream ? 5 : 4;
  const rateColumn = nrLabelColumns + 1;
  const firstWasteColumn = rateColumn + 1;
  const totalColumn = firstWasteColumn + wasteColumns.length;
  const unitLabel = UNIT_LABELS[unit];
  const numFmt = "#,##0.00";

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(
    getFileName("exportLabels:reportingSheetFileName", timeframe),
    {
      views: [
        { state: "frozen", ySplit: firstDataRow - 1, xSplit: nrLabelColumns },
      ],
    },
  );

  for (let col = 1; col <= totalColumn; col++) {
    sheet.getColumn(col).width = COL_WIDTH;
  }

  // Time period and selection in the top left corner.
  const period = `${formatTimeframeDate(timeframe.startTimeISO, "DD.MM.YYYY")}-${formatTimeframeDate(
    timeframe.endTimeISO,
    "DD.MM.YYYY",
  )}`;
  sheet.getRow(1).height = ROW_HEIGHT;
  sheet.getRow(2).height = ROW_HEIGHT;
  mergeLabelCells(sheet, 1, nrLabelColumns);
  sheet.getCell(1, 1).value = translate("exportLabels:timePeriod", { period });
  sheet.getCell(1, 1).style = { font: { italic: true } };
  mergeLabelCells(sheet, 2, nrLabelColumns);
  sheet.getCell(2, 1).value = translate("exportLabels:selection", {
    selection,
  });

  // Title over the data columns.
  sheet.mergeCells(1, rateColumn, 2, Math.max(totalColumn, rateColumn + 3));
  sheet.getCell(1, rateColumn).value = `${title} (${unitLabel})`;
  sheet.getCell(1, rateColumn).style = { font: { bold: true, size: 24 } };

  // Column titles.
  const headerRow = sheet.getRow(3);
  headerRow.height = ROW_HEIGHT;
  headerRow.values = [
    ...labelColumns.map((column) => translate(column.header)),
    translate("statisticsLabels:recyclingRate"),
    ...wasteColumns.map((column) => `${column.label} (${column.code})`),
    `${translate(isIndividual ? "reportingPage:reportingColumnRowTotal" : "reportingPage:reportingColumnTotal")} (${unitLabel})`,
  ];
  headerRow.font = { bold: true };

  // Downstream handling per waste type.
  if (showDownstream) {
    sheet.getRow(4).height = ROW_HEIGHT;
    mergeLabelCells(sheet, 4, nrLabelColumns);
    sheet.getCell(4, 1).value = translate("exportLabels:downstreamHandling");
    sheet.getCell(4, 1).style = { font: { bold: true } };
    wasteColumns.forEach((column, i) => {
      sheet.getCell(4, firstWasteColumn + i).value = getDownstreamName(
        column,
        flatDownstreamData,
      );
    });
  }

  const writeValues = (rowIndex: number, row: TableRow, bold: boolean) => {
    const rateCell = sheet.getCell(rowIndex, rateColumn);
    rateCell.value = row.rate / 100;
    rateCell.numFmt = "0.0%";

    const values = [
      ...wasteColumns.map((column) => row.cells[column.code] ?? 0),
      row.total,
    ];
    values.forEach((value, i) => {
      const cell = sheet.getCell(rowIndex, firstWasteColumn + i);
      // Empty values are shown as "-", as on the old reporting export.
      cell.value = value ? value : "-";
      cell.numFmt = numFmt;
    });

    for (let col = rateColumn; col <= totalColumn; col++) {
      const cell = sheet.getCell(rowIndex, col);
      cell.alignment = { horizontal: "right" };
      if (bold || col === totalColumn) cell.font = { bold: true };
    }
  };

  // Data rows.
  rows.forEach((row, i) => {
    const rowIndex = i + firstDataRow;
    sheet.getRow(rowIndex).height = ROW_HEIGHT - 5;
    labelColumns.forEach((column, col) => {
      sheet.getCell(rowIndex, col + 1).value = column.value(row);
    });
    writeValues(rowIndex, row, false);
  });

  // Totals row.
  if (rows.length > 1) {
    const rowIndex = rows.length + firstDataRow;
    sheet.getRow(rowIndex).height = ROW_HEIGHT;
    mergeLabelCells(sheet, rowIndex, nrLabelColumns);
    sheet.getCell(rowIndex, 1).value = translate(
      "reportingPage:reportingColumnTotal",
    );
    writeValues(rowIndex, totalsRow, true);
    for (let col = 1; col <= totalColumn; col++) {
      const cell = sheet.getCell(rowIndex, col);
      cell.font = { bold: true, size: 14 };
      cell.border = TOP_BORDER;
    }
  }

  return workbook;
};

export const exportReportingExcel = async (props: ReportingExportProps) => {
  const workbook = generateExcelFile(props);
  const xls = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([xls], { type: EXCEL_FORMAT }),
    getFileName(FILE_NAME_KEYS[props.unit], props.timeframe),
  );
};
